/**
 * PATH: apps/quote/src/components/builder/panels/inspector/AdjustedRateRow.tsx
 * FILE_ID: QB-INSPECTOR-OVERRIDES-ADJUSTED-RATE-ROW-001
 * PURPOSE: Adjusted-rate input row inside the Manual Overrides tab.
 * WHY: Same lifting motivation as MarkupOverrideRow — keeps OverridesTab
 *      under the 300-line file-size limit and consolidates the
 *      controlled-input + commit-on-blur + Escape-revert + Clear pattern
 *      in one place for the dollar-rate override.
 * FLOW:
 *   ┌──────────────┐    ┌─────────────────┐    ┌────────────────────────┐
 *   │ user edits   │ →  │ controlled text │ →  │ onCommit({adjusted_… }) │
 *   │ rate ($)     │    │ + Escape revert │    │ Clear → onClear()       │
 *   └──────────────┘    └─────────────────┘    └────────────────────────┘
 * DEPENDENCIES:
 *   - overrides-helpers: parseNumericInput + toFieldText
 *   - formatCurrency: live "calculated rate" hint
 * RELATED:
 *   - OverridesTab.tsx — composes this row alongside MarkupOverrideRow.
 */

import { useEffect, useState } from 'react'
import { RotateCcw } from 'lucide-react'
import { formatCurrency } from '@/lib/utils'
import { parseNumericInput, toFieldText } from './overrides-helpers'

export interface AdjustedRateRowProps {
  /** Current value on the line item (or null). */
  value: string | number | null | undefined
  /** Unit string for the label suffix ($/m², $/ea, etc.). */
  unit: string
  /** Calculated rate from the engine — displayed as a hint below input. */
  calculatedRate: number
  /** Called with a parsed number on blur/Enter; null is never sent (use onClear). */
  onCommit?: (rate: number) => void
  /** Called when the Clear affordance is pressed. */
  onClear?: () => void
  disabled?: boolean
}

export default function AdjustedRateRow({
  value,
  unit,
  calculatedRate,
  onCommit,
  onClear,
  disabled,
}: AdjustedRateRowProps) {
  const [text, setText] = useState<string>(toFieldText(value))

  useEffect(() => {
    setText(toFieldText(value))
  }, [value])

  const isReadOnly = disabled || !onCommit
  const hasValue = value != null && Number.parseFloat(String(value)) > 0

  const commit = () => {
    if (!onCommit) return
    const current = toFieldText(value)
    if (text === current) return
    const parsed = parseNumericInput(text)
    if (parsed === undefined || parsed === null) {
      setText(current)
      return
    }
    onCommit(parsed)
  }

  const handleClear = () => {
    setText('')
    onClear?.()
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label
          className="block text-xs font-medium text-slate-700 dark:text-slate-300"
          htmlFor="overrides-adjusted-rate"
        >
          Adjusted Rate ($/{unit})
        </label>
        {hasValue && !isReadOnly && onClear && (
          <button
            type="button"
            onClick={handleClear}
            className="flex items-center gap-1 text-[10px] text-slate-500 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 px-1.5 py-0.5 rounded transition-colors"
            title="Clear adjusted rate — row reverts to calculated rate"
            data-testid="overrides-clear-adjusted-rate"
          >
            <RotateCcw className="w-3 h-3" />
            Clear
          </button>
        )}
      </div>
      <input
        id="overrides-adjusted-rate"
        type="number"
        inputMode="decimal"
        step="any"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            ;(e.target as HTMLInputElement).blur()
          } else if (e.key === 'Escape') {
            setText(toFieldText(value))
            ;(e.target as HTMLInputElement).blur()
          }
        }}
        placeholder="Leave blank for calculated"
        disabled={isReadOnly}
        className="w-full px-2.5 py-1.5 border border-slate-200 dark:border-slate-700 rounded-md bg-white dark:bg-slate-800 text-sm disabled:opacity-60"
        data-testid="overrides-adjusted-rate-input"
      />
      <p className="text-xs text-slate-500 mt-1">
        Calculated rate: {formatCurrency(calculatedRate)}/{unit}
      </p>
    </div>
  )
}
